/*
================================================================================
FILE HEADER — apps/studio-web/src/lib/webCommands.ts
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                      | What Changed         |
|----|------------|-------------------|--------------------------|----------------------|
| 1  | 2026-06-25 | Claude Sonnet 4.6 | Custom Commands (Option C)| All web command      |
|    |            |                   |                          | definitions + IR     |
|    |            |                   |                          | step builders        |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/components/CustomCommandsPanel.tsx

EXPORTS COVERED
---------------
- CommandFieldType  — type of a command form field
- CommandField      — single field descriptor
- WebCommand        — one command (metadata + field schema + IR builder)
- WEB_COMMANDS      — full list of all commands, grouped by tab
- COMMAND_TABS      — ordered tab list
- parseSelector     — convert captured/manual selector JSON to IR Selector

IMPORTANT POINTS
----------------
1. buildSteps() returns IRStep[] (plural!) because some commands generate
   multiple steps (e.g., "Type Into Field" → tap + typeText).
2. For commands not directly in the IR (hover, scroll, browser back, etc.)
   we generate a `script` step with pre-written JS. This ensures they work
   both in the extension-based replay AND via `plex run` with Playwright.
3. The `selector` field type stores a JSON string of the IR Selector object.
   parseSelector() converts it to a usable Selector.
4. Extended SendKeys commands use `script` steps with KeyboardEvent dispatch
   since the IR pressKey only supports 5 keys. Chrome's CDP path in background.ts
   is also reliable but this approach works everywhere.
================================================================================
*/

import type { IRStep, Selector, Candidate } from '@plex/ir';
import { newId } from '@plex/ir';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type CommandFieldType =
  | 'selector'   // element picker (tap on mirror or enter locator manually)
  | 'text'       // single-line text input
  | 'number'     // numeric input
  | 'select'     // fixed-choice dropdown
  | 'key'        // keyboard key picker
  | 'code'       // multiline code textarea
  | 'variable';  // variable name (${var} syntax)

export interface CommandField {
  id:           string;
  label:        string;
  type:         CommandFieldType;
  placeholder?: string;
  required?:    boolean;
  options?:     Array<{ value: string; label: string }>;
  defaultValue?: string;
  hint?:        string;
}

export interface WebCommand {
  id:          string;
  tab:         string;
  name:        string;
  icon:        string;
  description: string;
  fields:      CommandField[];
  /** Build one or more IR steps from the form values.
   *  Returns IRStep[] so complex commands can emit multiple steps. */
  buildSteps:  (values: Record<string, string>) => IRStep[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

export function parseSelector(selectorJson: string, fallbackDesc = 'element'): Selector {
  try {
    const parsed = JSON.parse(selectorJson) as Selector;
    if (parsed.candidates && Array.isArray(parsed.candidates)) return parsed;
  } catch { /* fall through */ }

  // Treat raw string as a CSS selector if not valid JSON
  const value = selectorJson.trim();
  if (!value) return { description: fallbackDesc, candidates: [{ strategy: 'css', value: 'body', addedBy: 'user' }] };
  const strategy: Candidate['strategy'] = value.startsWith('/') ? 'xpath' : 'css';
  return { description: fallbackDesc, candidates: [{ strategy, value, addedBy: 'user' }] };
}

function step<T extends Omit<IRStep, 'id'>>(s: T): IRStep {
  return { id: newId(), ...s } as IRStep;
}

/** Normalise a URL entered by the user — auto-prepend https:// if no scheme. */
function normaliseUrl(raw: string): string {
  const s = raw.trim();
  if (!s) return s;
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
}

/** Build a script step with JS that dispatches a keyboard event. */
function keyScript(key: string, code: string, opts?: { ctrl?: boolean; shift?: boolean; alt?: boolean }): IRStep {
  const mods = `ctrlKey:${opts?.ctrl ?? false},shiftKey:${opts?.shift ?? false},altKey:${opts?.alt ?? false}`;
  const js = `(function(){var e=document.activeElement||document.body;`
    + `e.dispatchEvent(new KeyboardEvent('keydown',{key:'${key}',code:'${code}',${mods},bubbles:true,cancelable:true}));`
    + `e.dispatchEvent(new KeyboardEvent('keyup',  {key:'${key}',code:'${code}',${mods},bubbles:true}));})()`;
  return step({ type: 'script', label: `Key: ${key}`, code: js });
}

// ─────────────────────────────────────────────────────────────────────────────
// Command definitions
// ─────────────────────────────────────────────────────────────────────────────

// Special script codes recognised by background.ts for CDP-level handling.
// Using unique sentinel strings avoids ambiguity with user-written JS.
const CDP_BACK    = '__PLEX_CDP_BACK__';
const CDP_FORWARD = '__PLEX_CDP_FORWARD__';
const CDP_RELOAD  = '__PLEX_CDP_RELOAD__';

const NAVIGATE_COMMANDS: WebCommand[] = [
  {
    id: 'navigateTo', tab: 'Navigate', icon: '🌐', name: 'Navigate To',
    description: 'Open a URL in the current browser tab',
    fields: [{ id: 'url', label: 'URL', type: 'text', placeholder: 'https://example.com or www.example.com', required: true, hint: 'https:// is added automatically if missing' }],
    // Fix 1: normalise URL so www.google.com → https://www.google.com before openUrl validation
    buildSteps: (v) => {
      const url = normaliseUrl(v.url ?? '');
      return [step({ type: 'openUrl', label: `Navigate to ${url}`, url })];
    },
  },
  {
    id: 'browserBack', tab: 'Navigate', icon: '⬅️', name: 'Browser Back',
    description: 'Go back to the previous page (like clicking the Back button)',
    fields: [],
    // Fix 2: sentinel code handled by background.ts using CDP Page.navigateToHistoryEntry
    buildSteps: () => [step({ type: 'script', label: 'Browser Back', code: CDP_BACK })],
  },
  {
    id: 'browserForward', tab: 'Navigate', icon: '➡️', name: 'Browser Forward',
    description: 'Go forward to the next page',
    fields: [],
    // Fix 2: sentinel code handled by background.ts using CDP Page.navigateToHistoryEntry
    buildSteps: () => [step({ type: 'script', label: 'Browser Forward', code: CDP_FORWARD })],
  },
  {
    id: 'browserRefresh', tab: 'Navigate', icon: '🔄', name: 'Refresh Page',
    description: 'Reload the current page',
    fields: [],
    // Use CDP Page.reload sentinel so we can wait for load event
    buildSteps: () => [step({ type: 'script', label: 'Refresh Page', code: CDP_RELOAD })],
  },
  {
    id: 'deepLink', tab: 'Navigate', icon: '🔗', name: 'Deep Link',
    description: 'Navigate to a path relative to the current origin (e.g. /settings?tab=profile)',
    fields: [{ id: 'path', label: 'Path', type: 'text', placeholder: '/dashboard?user=123', required: true, hint: 'Must start with /' }],
    // Fix 3: sentinel prefix tells background.ts to use CDP navigate + wait for load
    buildSteps: (v) => {
      const path = v.path?.startsWith('/') ? v.path : `/${v.path ?? ''}`;
      return [step({ type: 'script', label: `Deep Link: ${path}`, code: `__PLEX_CDP_DEEPLINK__${path}` })];
    },
  },
  {
    id: 'openBrowser', tab: 'Navigate', icon: '🪟', name: 'Open New Tab',
    description: 'Open a URL in a new browser tab. Optionally switch the mirror to the new tab.',
    fields: [
      { id: 'url',       label: 'URL', type: 'text', placeholder: 'https://example.com', required: true },
      { id: 'switchTab', label: 'Switch mirror to new tab', type: 'select', defaultValue: 'stay',
        options: [
          { value: 'stay',   label: 'Stay on current tab (default)' },
          { value: 'switch', label: 'Switch mirror to new tab' },
        ] },
    ],
    // Sentinel: __PLEX_CDP_NEWTAB__<url>|<stay|switch>
    buildSteps: (v) => {
      const url    = normaliseUrl(v.url ?? '');
      const action = v.switchTab ?? 'stay';
      const label  = action === 'switch' ? `Open & switch to ${url}` : `Open tab: ${url}`;
      return [step({ type: 'script', label, code: `__PLEX_CDP_NEWTAB__${url}|${action}` })];
    },
  },
  {
    id: 'switchMirror', tab: 'Navigate', icon: '🔀', name: 'Switch Mirror',
    description: 'Switch the mirror (debugger) to a different open tab. All subsequent steps run on that tab.',
    fields: [
      { id: 'matchBy', label: 'Find tab by', type: 'select', defaultValue: 'url',
        options: [
          { value: 'url',    label: 'URL contains' },
          { value: 'title',  label: 'Tab title contains' },
          { value: 'newest', label: 'Newest open tab' },
        ] },
      { id: 'value', label: 'Match value', type: 'text', placeholder: 'e.g. youtube.com or "My App - Dashboard"',
        hint: 'Not needed when "Newest open tab" is selected' },
    ],
    // Sentinel: __PLEX_CDP_SWITCHTAB__<url|title|newest>|<value>
    buildSteps: (v) => {
      const by    = v.matchBy ?? 'url';
      const val   = v.value?.trim() ?? '';
      const label = by === 'newest' ? 'Switch mirror → newest tab' : `Switch mirror → tab ${by} contains "${val}"`;
      return [step({ type: 'script', label, code: `__PLEX_CDP_SWITCHTAB__${by}|${val}` })];
    },
  },
  {
    id: 'closeBrowser', tab: 'Navigate', icon: '✕', name: 'Close Tab',
    description: 'Close a background tab (never closes the mirrored tab — that would break execution).',
    fields: [
      { id: 'matchBy', label: 'Close which tab', type: 'select', defaultValue: 'url',
        options: [
          { value: 'url',    label: 'URL contains' },
          { value: 'title',  label: 'Tab title contains' },
          { value: 'newest', label: 'Newest non-mirrored tab' },
        ] },
      { id: 'value', label: 'Match value', type: 'text', placeholder: 'e.g. amazon.com',
        hint: 'Not needed when "Newest non-mirrored tab" is selected' },
    ],
    // Sentinel: __PLEX_CDP_CLOSETAB__<url|title|newest>|<value>
    buildSteps: (v) => {
      const by    = v.matchBy ?? 'url';
      const val   = v.value?.trim() ?? '';
      const label = by === 'newest' ? 'Close newest background tab'
                  : `Close tab where ${by} contains "${val}"`;
      return [step({ type: 'script', label, code: `__PLEX_CDP_CLOSETAB__${by}|${val}` })];
    },
  },
  {
    id: 'getCurrentUrl', tab: 'Navigate', icon: '📍', name: 'Get Current URL',
    description: 'Store the current page URL in a variable',
    fields: [{ id: 'storeAs', label: 'Store as variable', type: 'variable', placeholder: 'currentUrl', required: true }],
    buildSteps: (v) => [step({ type: 'script', label: `Get URL → ${v.storeAs ?? ''}`, code: 'window.location.href', storeAs: v.storeAs })],
  },
];

const INPUT_COMMANDS: WebCommand[] = [
  {
    id: 'click', tab: 'Input', icon: '👆', name: 'Click',
    description: 'Click on an element',
    fields: [{ id: 'selector', label: 'Element', type: 'selector', required: true }],
    buildSteps: (v) => [step({ type: 'tap', label: `Click ${parseSelector(v.selector ?? '').description}`, selector: parseSelector(v.selector ?? '') })],
  },
  {
    id: 'doubleClick', tab: 'Input', icon: '👆👆', name: 'Double Click',
    description: 'Double-click on an element',
    fields: [{ id: 'selector', label: 'Element', type: 'selector', required: true }],
    buildSteps: (v) => [step({ type: 'doubleTap', label: `Double-click ${parseSelector(v.selector ?? '').description}`, selector: parseSelector(v.selector ?? '') })],
  },
  {
    id: 'rightClick', tab: 'Input', icon: '🖱️', name: 'Right Click',
    description: 'Right-click an element to open context menu',
    fields: [{ id: 'selector', label: 'Element', type: 'selector', required: true }],
    buildSteps: (v) => {
      const sel = parseSelector(v.selector ?? '');
      const cssSel = JSON.stringify(sel.candidates[0]?.value ?? 'body');
      return [step({ type: 'script', label: `Right-click ${sel.description}`,
        code: `(function(){var e=document.querySelector(${cssSel});if(e){var r=e.getBoundingClientRect();e.dispatchEvent(new MouseEvent('contextmenu',{bubbles:true,clientX:r.left+r.width/2,clientY:r.top+r.height/2}));}})()` })];
    },
  },
  {
    id: 'hover', tab: 'Input', icon: '🎯', name: 'Hover',
    description: 'Hover the mouse over an element (triggers hover/tooltip effects)',
    fields: [{ id: 'selector', label: 'Element', type: 'selector', required: true }],
    buildSteps: (v) => {
      const sel = parseSelector(v.selector ?? '');
      const cssSel = JSON.stringify(sel.candidates[0]?.value ?? 'body');
      return [step({ type: 'script', label: `Hover ${sel.description}`,
        code: `(function(){var e=document.querySelector(${cssSel});if(e){var r=e.getBoundingClientRect();var mo=new MouseEvent('mouseover',{bubbles:true,clientX:r.left+r.width/2,clientY:r.top+r.height/2});e.dispatchEvent(mo);}})()` })];
    },
  },
  {
    id: 'typeIntoField', tab: 'Input', icon: '⌨️', name: 'Type Into Field',
    description: 'Click an input, then type text into it',
    fields: [
      { id: 'selector', label: 'Input field', type: 'selector', required: true },
      { id: 'text',     label: 'Text to type', type: 'text', placeholder: 'Hello world', required: true },
    ],
    buildSteps: (v) => [
      step({ type: 'tap',      label: `Focus: ${parseSelector(v.selector ?? '').description}`, selector: parseSelector(v.selector ?? '') }),
      step({ type: 'typeText', label: `Type: ${v.text ?? ''}`, text: v.text ?? '' }),
    ],
  },
  {
    id: 'clearText', tab: 'Input', icon: '🗑️', name: 'Clear Text',
    description: 'Clear all text from an input field',
    fields: [{ id: 'selector', label: 'Input field', type: 'selector', required: true }],
    buildSteps: (v) => [step({ type: 'clearText', label: `Clear: ${parseSelector(v.selector ?? '').description}`, selector: parseSelector(v.selector ?? '') })],
  },
  {
    id: 'selectDropdown', tab: 'Input', icon: '📋', name: 'Select Dropdown',
    description: 'Select an option from a <select> dropdown',
    fields: [
      { id: 'selector', label: 'Dropdown element', type: 'selector', required: true },
      { id: 'value',    label: 'Option value or text', type: 'text', placeholder: 'option_value', required: true },
      { id: 'by',       label: 'Match by', type: 'select', defaultValue: 'value',
        options: [{ value: 'value', label: 'Value attribute' }, { value: 'text', label: 'Visible text' }, { value: 'index', label: 'Index (0-based)' }] },
    ],
    buildSteps: (v) => {
      const sel = parseSelector(v.selector ?? '');
      const cssSel = JSON.stringify(sel.candidates[0]?.value ?? 'select');
      const val    = JSON.stringify(v.value ?? '');
      const by     = v.by ?? 'value';
      const code = by === 'value'
        ? `(function(){var e=document.querySelector(${cssSel});if(e){e.value=${val};e.dispatchEvent(new Event('change',{bubbles:true}));}})()`
        : by === 'index'
          ? `(function(){var e=document.querySelector(${cssSel});if(e){e.selectedIndex=${v.value ?? 0};e.dispatchEvent(new Event('change',{bubbles:true}));}})()`
          : `(function(){var e=document.querySelector(${cssSel});if(e){for(var o of e.options){if(o.text.trim()===${val}){o.selected=true;break;}}e.dispatchEvent(new Event('change',{bubbles:true}));}})()`;
      return [step({ type: 'script', label: `Select ${v.value ?? ''} in ${sel.description}`, code })];
    },
  },
  {
    id: 'focus', tab: 'Input', icon: '🎯', name: 'Focus Element',
    description: 'Move keyboard focus to an element',
    fields: [{ id: 'selector', label: 'Element', type: 'selector', required: true }],
    buildSteps: (v) => {
      const sel    = parseSelector(v.selector ?? '');
      const cssSel = JSON.stringify(sel.candidates[0]?.value ?? 'body');
      return [step({ type: 'script', label: `Focus ${sel.description}`, code: `document.querySelector(${cssSel})?.focus()` })];
    },
  },
];

const KEY_OPTIONS = [
  { value: 'Enter',      label: 'Enter ↵'      },
  { value: 'Backspace',  label: 'Backspace ⌫'  },
  { value: 'Delete',     label: 'Delete ⌦'     },
  { value: 'Escape',     label: 'Escape ⎋'     },
  { value: 'Tab',        label: 'Tab ⇥'        },
  { value: 'Space',      label: 'Space ␣'       },
  { value: 'ArrowUp',    label: 'Arrow Up ↑'   },
  { value: 'ArrowDown',  label: 'Arrow Down ↓' },
  { value: 'ArrowLeft',  label: 'Arrow Left ←' },
  { value: 'ArrowRight', label: 'Arrow Right →' },
  { value: 'Home',       label: 'Home ⤒'       },
  { value: 'End',        label: 'End ⤓'        },
  { value: 'PageUp',     label: 'Page Up'      },
  { value: 'PageDown',   label: 'Page Down'    },
  { value: 'F5',         label: 'F5 (Refresh)' },
  { value: 'F12',        label: 'F12 (DevTools)'},
];

const KEY_CODE_MAP: Record<string, string> = {
  Enter: 'Enter', Backspace: 'Backspace', Delete: 'Delete', Escape: 'Escape',
  Tab: 'Tab', Space: 'Space', ArrowUp: 'ArrowUp', ArrowDown: 'ArrowDown',
  ArrowLeft: 'ArrowLeft', ArrowRight: 'ArrowRight', Home: 'Home', End: 'End',
  PageUp: 'PageUp', PageDown: 'PageDown', F5: 'F5', F12: 'F12',
};

const KEYS_COMMANDS: WebCommand[] = [
  {
    id: 'sendKeys', tab: 'Keys', icon: '⌨️', name: 'Send Key',
    description: 'Press a keyboard key on the currently focused element',
    fields: [{ id: 'key', label: 'Key', type: 'select', required: true, defaultValue: 'Enter', options: KEY_OPTIONS }],
    buildSteps: (v) => {
      const key = v.key ?? 'Enter';
      // pressKey IR for 5 natively supported keys, script for the rest
      if (key === 'Enter')  return [step({ type: 'pressKey', label: 'Press Enter',     key: 'enter' })];
      if (key === 'Tab')    return [step({ type: 'pressKey', label: 'Press Tab',       key: 'tab' })];
      if (key === 'Delete') return [step({ type: 'pressKey', label: 'Press Delete',    key: 'delete' })];
      if (key === 'Backspace') return [step({ type: 'script', label: 'Press Backspace',
        code: `(function(){var e=document.activeElement||document.body;e.dispatchEvent(new KeyboardEvent('keydown',{key:'Backspace',code:'Backspace',bubbles:true}));e.dispatchEvent(new KeyboardEvent('keyup',{key:'Backspace',code:'Backspace',bubbles:true}));})()` })];
      return [keyScript(key, KEY_CODE_MAP[key] ?? key)];
    },
  },
  {
    id: 'keyCombo', tab: 'Keys', icon: '⌘', name: 'Key Combination',
    description: 'Press a keyboard shortcut (e.g., Ctrl+A, Ctrl+C)',
    fields: [
      { id: 'modifier', label: 'Modifier', type: 'select', required: true, defaultValue: 'ctrl',
        options: [{ value: 'ctrl', label: 'Ctrl (Cmd on Mac)' }, { value: 'shift', label: 'Shift' }, { value: 'alt', label: 'Alt' }, { value: 'ctrl+shift', label: 'Ctrl + Shift' }] },
      { id: 'key', label: 'Key', type: 'text', placeholder: 'a', required: true, hint: 'e.g. a, c, v, z, x' },
    ],
    buildSteps: (v) => {
      const mod = v.modifier ?? 'ctrl';
      const key = (v.key ?? 'a').toUpperCase();
      const code = JSON.stringify(`(function(){var e=document.activeElement||document.body;e.dispatchEvent(new KeyboardEvent('keydown',{key:'${v.key ?? 'a'}',ctrlKey:${mod.includes('ctrl')},shiftKey:${mod.includes('shift')},altKey:${mod.includes('alt')},bubbles:true}));})();`);
      return [step({ type: 'script', label: `${mod.toUpperCase()}+${key}`, code: JSON.parse(code) })];
    },
  },
  {
    id: 'clearField', tab: 'Keys', icon: '✕', name: 'Clear Field (Ctrl+A, Delete)',
    description: 'Select all text in focused field and delete it',
    fields: [],
    buildSteps: () => [step({ type: 'script', label: 'Clear focused field',
      code: `(function(){var e=document.activeElement;if(e){e.dispatchEvent(new KeyboardEvent('keydown',{key:'a',ctrlKey:true,bubbles:true}));e.dispatchEvent(new KeyboardEvent('keydown',{key:'Delete',bubbles:true}));}})()` })],
  },
  {
    id: 'selectAll', tab: 'Keys', icon: '▣', name: 'Select All',
    description: 'Select all text in the focused element (Ctrl+A)',
    fields: [],
    buildSteps: () => [step({ type: 'script', label: 'Select All (Ctrl+A)',
      code: `document.execCommand('selectAll',false,null)||(document.activeElement&&document.activeElement.dispatchEvent(new KeyboardEvent('keydown',{key:'a',ctrlKey:true,bubbles:true})))` })],
  },
  {
    id: 'copyText', tab: 'Keys', icon: '📋', name: 'Copy (Ctrl+C)',
    description: 'Copy the current selection to clipboard',
    fields: [],
    buildSteps: () => [step({ type: 'script', label: 'Copy (Ctrl+C)', code: `document.execCommand('copy')` })],
  },
  {
    id: 'pasteText', tab: 'Keys', icon: '📄', name: 'Paste (Ctrl+V)',
    description: 'Paste clipboard contents into focused element',
    fields: [],
    buildSteps: () => [step({ type: 'script', label: 'Paste (Ctrl+V)', code: `document.execCommand('paste')` })],
  },
];

const ASSERT_COMMANDS: WebCommand[] = [
  {
    id: 'assertVisible', tab: 'Assert', icon: '👁️', name: 'Element Visible',
    description: 'Assert that an element is present and visible on screen',
    fields: [{ id: 'selector', label: 'Element', type: 'selector', required: true }],
    buildSteps: (v) => [step({ type: 'assert', label: `Assert visible: ${parseSelector(v.selector ?? '').description}`, selector: parseSelector(v.selector ?? ''), assertion: 'visible' })],
  },
  {
    id: 'assertHidden', tab: 'Assert', icon: '🙈', name: 'Element Hidden',
    description: 'Assert that an element is NOT visible or not in the DOM',
    fields: [{ id: 'selector', label: 'Element', type: 'selector', required: true }],
    buildSteps: (v) => [step({ type: 'assert', label: `Assert hidden: ${parseSelector(v.selector ?? '').description}`, selector: parseSelector(v.selector ?? ''), assertion: 'notVisible' })],
  },
  {
    id: 'assertEnabled', tab: 'Assert', icon: '✅', name: 'Element Enabled',
    description: 'Assert that an element is interactive (not disabled)',
    fields: [{ id: 'selector', label: 'Element', type: 'selector', required: true }],
    buildSteps: (v) => [step({ type: 'assert', label: `Assert enabled: ${parseSelector(v.selector ?? '').description}`, selector: parseSelector(v.selector ?? ''), assertion: 'enabled' })],
  },
  {
    id: 'assertDisabled', tab: 'Assert', icon: '🚫', name: 'Element Disabled',
    description: 'Assert that an element is disabled / non-interactive',
    fields: [{ id: 'selector', label: 'Element', type: 'selector', required: true }],
    buildSteps: (v) => [step({ type: 'assert', label: `Assert disabled: ${parseSelector(v.selector ?? '').description}`, selector: parseSelector(v.selector ?? ''), assertion: 'disabled' })],
  },
  {
    id: 'assertText', tab: 'Assert', icon: '🔤', name: 'Text Contains',
    description: 'Assert that an element\'s text contains the expected string',
    fields: [
      { id: 'selector', label: 'Element', type: 'selector', required: true },
      { id: 'value', label: 'Expected text', type: 'text', placeholder: 'Welcome, Jane', required: true },
    ],
    buildSteps: (v) => [step({ type: 'assert', label: `Assert text: "${v.value ?? ''}"`, selector: parseSelector(v.selector ?? ''), assertion: 'hasText', value: v.value })],
  },
  {
    id: 'assertUrl', tab: 'Assert', icon: '🌐', name: 'URL Contains',
    description: 'Assert that the current page URL contains a string',
    fields: [{ id: 'value', label: 'Expected URL part', type: 'text', placeholder: '/dashboard', required: true }],
    buildSteps: (v) => [step({ type: 'script', label: `Assert URL contains "${v.value ?? ''}"`,
      code: `(function(){var u=window.location.href;if(!u.includes(${JSON.stringify(v.value??'')}))throw new Error('URL "'+u+'" does not contain "'+${JSON.stringify(v.value??'')}+'"');})()` })],
  },
  {
    id: 'assertTitle', tab: 'Assert', icon: '📄', name: 'Page Title',
    description: 'Assert that the page title contains a string',
    fields: [{ id: 'value', label: 'Expected title', type: 'text', placeholder: 'My App - Dashboard', required: true }],
    buildSteps: (v) => [step({ type: 'script', label: `Assert title contains "${v.value ?? ''}"`,
      code: `(function(){var t=document.title;if(!t.includes(${JSON.stringify(v.value??'')}))throw new Error('Title "'+t+'" does not match');})()` })],
  },
  {
    id: 'assertCount', tab: 'Assert', icon: '🔢', name: 'Element Count',
    description: 'Assert that exactly N elements match a selector',
    fields: [
      { id: 'selector', label: 'Selector (CSS)', type: 'text', placeholder: '.list-item', required: true },
      { id: 'count',    label: 'Expected count', type: 'number', placeholder: '3', required: true },
    ],
    buildSteps: (v) => [step({ type: 'script', label: `Assert count ${v.count ?? 0}: ${v.selector ?? ''}`,
      code: `(function(){var n=document.querySelectorAll(${JSON.stringify(v.selector??'')}).length;if(n!==${parseInt(v.count??'0',10)})throw new Error('Expected ${v.count} elements, found '+n);})()` })],
  },
  {
    id: 'assertAttribute', tab: 'Assert', icon: '🏷️', name: 'Attribute Value',
    description: 'Assert that an element attribute equals an expected value',
    fields: [
      { id: 'selector',   label: 'Element', type: 'selector', required: true },
      { id: 'attribute',  label: 'Attribute name', type: 'text', placeholder: 'href', required: true },
      { id: 'value',      label: 'Expected value', type: 'text', placeholder: '/home', required: true },
    ],
    buildSteps: (v) => {
      const sel    = parseSelector(v.selector ?? '');
      const cssSel = JSON.stringify(sel.candidates[0]?.value ?? 'body');
      return [step({ type: 'script', label: `Assert ${v.attribute ?? ''}="${v.value ?? ''}"`,
        code: `(function(){var e=document.querySelector(${cssSel});var a=e&&e.getAttribute(${JSON.stringify(v.attribute??'')});if(a!==${JSON.stringify(v.value??'')})throw new Error('Expected attr ${v.attribute}="${v.value}", got "'+a+'"');})()` })];
    },
  },
];

const WAIT_COMMANDS: WebCommand[] = [
  {
    id: 'fixedWait', tab: 'Wait', icon: '⏸️', name: 'Fixed Wait',
    description: 'Pause execution for a fixed number of milliseconds',
    fields: [{ id: 'durationMs', label: 'Duration', type: 'number', placeholder: '1000', required: true, hint: 'milliseconds', defaultValue: '1000' }],
    buildSteps: (v) => [step({ type: 'wait', label: `Wait ${v.durationMs ?? 1000}ms`, durationMs: parseInt(v.durationMs ?? '1000', 10) })],
  },
  {
    id: 'waitForElement', tab: 'Wait', icon: '⏳', name: 'Wait for Element',
    description: 'Wait until an element appears on screen (up to timeout)',
    fields: [
      { id: 'selector',   label: 'Element', type: 'selector', required: true },
      { id: 'timeoutMs',  label: 'Timeout', type: 'number', placeholder: '10000', defaultValue: '10000', hint: 'ms' },
      { id: 'state',      label: 'Wait until', type: 'select', defaultValue: 'visible',
        options: [{ value: 'visible', label: 'Visible' }, { value: 'hidden', label: 'Hidden' }, { value: 'enabled', label: 'Enabled' }] },
    ],
    buildSteps: (v) => [step({ type: 'waitFor', label: `Wait for ${v.state ?? 'visible'}: ${parseSelector(v.selector ?? '').description}`,
      selector: parseSelector(v.selector ?? ''), state: (v.state ?? 'visible') as 'visible'|'hidden'|'enabled'|'disabled',
      timeoutMs: parseInt(v.timeoutMs ?? '10000', 10) })],
  },
  {
    id: 'waitForUrl', tab: 'Wait', icon: '🌐', name: 'Wait for URL',
    description: 'Wait until the page URL contains the expected string',
    fields: [
      { id: 'urlPattern', label: 'URL contains', type: 'text', placeholder: '/dashboard', required: true },
      { id: 'timeoutMs',  label: 'Timeout (ms)', type: 'number', defaultValue: '10000' },
    ],
    buildSteps: (v) => [step({ type: 'script', label: `Wait for URL: ${v.urlPattern ?? ''}`,
      code: `(function(){return new Promise(function(res,rej){var t=${parseInt(v.timeoutMs??'10000',10)},s=Date.now();(function c(){if(window.location.href.includes(${JSON.stringify(v.urlPattern??'')}))res();else if(Date.now()-s>t)rej(new Error('Timeout waiting for URL'));else setTimeout(c,200);})();});})()` })],
  },
  {
    id: 'waitForNetwork', tab: 'Wait', icon: '📡', name: 'Wait for Network Idle',
    description: 'Wait until there are no active network requests',
    fields: [{ id: 'timeoutMs', label: 'Timeout (ms)', type: 'number', defaultValue: '5000' }],
    buildSteps: (v) => [step({ type: 'waitForNetwork', label: 'Wait for network idle', timeoutMs: parseInt(v.timeoutMs ?? '5000', 10) })],
  },
];

const SCROLL_COMMANDS: WebCommand[] = [
  {
    id: 'scrollDown', tab: 'Scroll', icon: '⬇️', name: 'Scroll Down',
    description: 'Scroll down the page by a number of pixels',
    fields: [{ id: 'pixels', label: 'Pixels', type: 'number', placeholder: '300', defaultValue: '300' }],
    buildSteps: (v) => [step({ type: 'script', label: `Scroll down ${v.pixels ?? 300}px`, code: `window.scrollBy(0, ${parseInt(v.pixels ?? '300', 10)})` })],
  },
  {
    id: 'scrollUp', tab: 'Scroll', icon: '⬆️', name: 'Scroll Up',
    description: 'Scroll up the page by a number of pixels',
    fields: [{ id: 'pixels', label: 'Pixels', type: 'number', placeholder: '300', defaultValue: '300' }],
    buildSteps: (v) => [step({ type: 'script', label: `Scroll up ${v.pixels ?? 300}px`, code: `window.scrollBy(0, -${parseInt(v.pixels ?? '300', 10)})` })],
  },
  {
    id: 'pageTop', tab: 'Scroll', icon: '⏫', name: 'Page Top',
    description: 'Scroll to the very top of the page',
    fields: [],
    buildSteps: () => [step({ type: 'script', label: 'Scroll to top', code: `window.scrollTo({top:0,behavior:'smooth'})` })],
  },
  {
    id: 'pageBottom', tab: 'Scroll', icon: '⏬', name: 'Page Bottom',
    description: 'Scroll to the very bottom of the page',
    fields: [],
    buildSteps: () => [step({ type: 'script', label: 'Scroll to bottom', code: `window.scrollTo({top:document.body.scrollHeight,behavior:'smooth'})` })],
  },
  {
    id: 'scrollToElement', tab: 'Scroll', icon: '🎯', name: 'Scroll to Element',
    description: 'Scroll until a specific element is visible in the viewport',
    fields: [{ id: 'selector', label: 'Element', type: 'selector', required: true }],
    buildSteps: (v) => {
      const sel    = parseSelector(v.selector ?? '');
      const cssSel = JSON.stringify(sel.candidates[0]?.value ?? 'body');
      return [step({ type: 'script', label: `Scroll to ${sel.description}`, code: `document.querySelector(${cssSel})?.scrollIntoView({block:'center',behavior:'smooth'})` })];
    },
  },
];

const DATA_COMMANDS: WebCommand[] = [
  {
    id: 'setVariable', tab: 'Variables', icon: '📦', name: 'Set Variable',
    description: 'Assign a fixed value to a named variable for use in later steps',
    fields: [
      { id: 'name',  label: 'Variable name', type: 'variable', placeholder: 'myVar', required: true },
      { id: 'value', label: 'Value', type: 'text', placeholder: 'hello world', required: true },
    ],
    buildSteps: (v) => [step({ type: 'setVariable', label: `Set ${v.name ?? ''} = "${v.value ?? ''}"`, name: v.name ?? 'var', source: { kind: 'literal', value: v.value ?? '' } })],
  },
  {
    id: 'getText', tab: 'Variables', icon: '📄', name: 'Get Text',
    description: 'Extract the visible text of an element and store it in a variable',
    fields: [
      { id: 'selector', label: 'Element', type: 'selector', required: true },
      { id: 'storeAs',  label: 'Store as variable', type: 'variable', placeholder: 'elementText', required: true },
    ],
    buildSteps: (v) => [step({ type: 'getAttribute', label: `Get text → ${v.storeAs ?? ''}`, selector: parseSelector(v.selector ?? ''), attribute: 'innerText', storeAs: v.storeAs ?? 'text' })],
  },
  {
    id: 'getAttribute', tab: 'Variables', icon: '🏷️', name: 'Get Attribute',
    description: 'Extract an HTML attribute from an element into a variable',
    fields: [
      { id: 'selector',  label: 'Element', type: 'selector', required: true },
      { id: 'attribute', label: 'Attribute name', type: 'text', placeholder: 'href', required: true },
      { id: 'storeAs',   label: 'Store as variable', type: 'variable', placeholder: 'attrValue', required: true },
    ],
    buildSteps: (v) => [step({ type: 'getAttribute', label: `Get ${v.attribute ?? ''} → ${v.storeAs ?? ''}`, selector: parseSelector(v.selector ?? ''), attribute: v.attribute ?? 'value', storeAs: v.storeAs ?? 'attr' })],
  },
  {
    id: 'getVariable', tab: 'Variables', icon: '📤', name: 'Get Current URL',
    description: 'Store the current page URL in a variable',
    fields: [{ id: 'storeAs', label: 'Store as variable', type: 'variable', placeholder: 'currentUrl', required: true }],
    buildSteps: (v) => [step({ type: 'script', label: `Get URL → ${v.storeAs ?? ''}`, code: 'window.location.href', storeAs: v.storeAs })],
  },
  {
    id: 'getElementsCount', tab: 'Variables', icon: '🔢', name: 'Get Elements Count',
    description: 'Count matching elements and store the number in a variable',
    fields: [
      { id: 'selector', label: 'CSS selector', type: 'text', placeholder: '.list-item', required: true },
      { id: 'storeAs',  label: 'Store as variable', type: 'variable', placeholder: 'itemCount', required: true },
    ],
    buildSteps: (v) => [step({ type: 'getCount', label: `Count ${v.selector ?? ''} → ${v.storeAs ?? ''}`,
      selector: { description: v.selector ?? '', candidates: [{ strategy: 'css', value: v.selector ?? 'body', addedBy: 'user' }] },
      storeAs: v.storeAs ?? 'count' })],
  },
  {
    id: 'printLog', tab: 'Variables', icon: '📝', name: 'Print Log',
    description: 'Print a message to the test log (visible in reports)',
    fields: [
      { id: 'message', label: 'Message', type: 'text', placeholder: 'Step reached: login page', required: true },
      { id: 'level',   label: 'Level', type: 'select', defaultValue: 'info',
        options: [{ value: 'info', label: 'Info' }, { value: 'warn', label: 'Warning' }, { value: 'error', label: 'Error' }] },
    ],
    buildSteps: (v) => [step({ type: 'print', label: `Log: ${v.message ?? ''}`, message: v.message ?? '', level: (v.level ?? 'info') as 'info'|'warn'|'error' })],
  },
];

// ─── Additional Input commands (gap-fill from cheat sheet) ──────────────────

const INPUT_EXTRA_COMMANDS: WebCommand[] = [
  {
    id: 'checkBox', tab: 'Input', icon: '☑️', name: 'Check Checkbox',
    description: 'Check a checkbox or radio button (sets it to checked state)',
    fields: [{ id: 'selector', label: 'Checkbox element', type: 'selector', required: true }],
    buildSteps: (v) => {
      const sel = parseSelector(v.selector ?? '');
      const cssSel = JSON.stringify(sel.candidates[0]?.value ?? 'input[type="checkbox"]');
      return [step({ type: 'script', label: `Check ${sel.description}`,
        code: `(function(){var e=document.querySelector(${cssSel});if(e&&!e.checked){e.click();}})()` })];
    },
  },
  {
    id: 'uncheckBox', tab: 'Input', icon: '⬜', name: 'Uncheck Checkbox',
    description: 'Uncheck a checkbox (sets it to unchecked state)',
    fields: [{ id: 'selector', label: 'Checkbox element', type: 'selector', required: true }],
    buildSteps: (v) => {
      const sel = parseSelector(v.selector ?? '');
      const cssSel = JSON.stringify(sel.candidates[0]?.value ?? 'input[type="checkbox"]');
      return [step({ type: 'script', label: `Uncheck ${sel.description}`,
        code: `(function(){var e=document.querySelector(${cssSel});if(e&&e.checked){e.click();}})()` })];
    },
  },
  {
    id: 'blurElement', tab: 'Input', icon: '💤', name: 'Blur (Unfocus)',
    description: 'Remove focus from the currently focused element',
    fields: [{ id: 'selector', label: 'Element (optional — leave blank for active element)', type: 'selector' }],
    buildSteps: (v) => {
      const raw = v.selector?.trim();
      if (!raw) return [step({ type: 'script', label: 'Blur active element', code: `document.activeElement?.blur()` })];
      const sel = parseSelector(raw);
      const cssSel = JSON.stringify(sel.candidates[0]?.value ?? 'body');
      return [step({ type: 'script', label: `Blur ${sel.description}`, code: `document.querySelector(${cssSel})?.blur()` })];
    },
  },
  {
    id: 'dragAndDrop', tab: 'Input', icon: '🤏', name: 'Drag & Drop',
    description: 'Drag an element and drop it onto another element',
    fields: [
      { id: 'source', label: 'Drag from (source)', type: 'selector', required: true },
      { id: 'target', label: 'Drop onto (target)', type: 'selector', required: true },
    ],
    buildSteps: (v) => {
      const src = parseSelector(v.source ?? '');
      const tgt = parseSelector(v.target ?? '');
      const srcSel = JSON.stringify(src.candidates[0]?.value ?? 'body');
      const tgtSel = JSON.stringify(tgt.candidates[0]?.value ?? 'body');
      return [step({ type: 'script', label: `Drag ${src.description} → ${tgt.description}`,
        code: `(function(){
var s=document.querySelector(${srcSel}),t=document.querySelector(${tgtSel});
if(!s||!t)return;
var rs=s.getBoundingClientRect(),rt=t.getBoundingClientRect();
var dt=new DataTransfer();
s.dispatchEvent(new DragEvent('dragstart',{bubbles:true,dataTransfer:dt,clientX:rs.left+rs.width/2,clientY:rs.top+rs.height/2}));
t.dispatchEvent(new DragEvent('dragover',{bubbles:true,dataTransfer:dt,clientX:rt.left+rt.width/2,clientY:rt.top+rt.height/2}));
t.dispatchEvent(new DragEvent('drop',{bubbles:true,dataTransfer:dt,clientX:rt.left+rt.width/2,clientY:rt.top+rt.height/2}));
s.dispatchEvent(new DragEvent('dragend',{bubbles:true,dataTransfer:dt}));
})()` })];
    },
  },
  {
    id: 'fileUpload', tab: 'Input', icon: '📁', name: 'File Upload',
    description: 'Set the value of a file input element (path on local disk)',
    fields: [
      { id: 'selector', label: 'File input element', type: 'selector', required: true },
      { id: 'filePath', label: 'File path or URL', type: 'text', placeholder: '/path/to/file.pdf or data URL', required: true },
    ],
    buildSteps: (v) => [step({ type: 'script', label: `Upload file to ${parseSelector(v.selector ?? '').description}`,
      code: `/* NOTE: File upload requires Playwright path. Use plex run with setInputFiles. Path: ${v.filePath ?? ''} */` })],
  },
  {
    id: 'assertInputValue', tab: 'Assert', icon: '✏️', name: 'Input Value',
    description: 'Assert that an input field has a specific value',
    fields: [
      { id: 'selector', label: 'Input element', type: 'selector', required: true },
      { id: 'value', label: 'Expected value', type: 'text', placeholder: 'john@example.com', required: true },
    ],
    buildSteps: (v) => {
      const sel = parseSelector(v.selector ?? '');
      const cssSel = JSON.stringify(sel.candidates[0]?.value ?? 'input');
      return [step({ type: 'script', label: `Assert value: "${v.value ?? ''}"`,
        code: `(function(){var e=document.querySelector(${cssSel});var val=e&&(e.value!==undefined?e.value:e.textContent);if(val!==${JSON.stringify(v.value??'')})throw new Error('Expected value "${v.value}", got "'+val+'"');})()` })];
    },
  },
];

// ─── Storage commands (localStorage / sessionStorage / cookies) ───────────────

const STORAGE_COMMANDS: WebCommand[] = [
  {
    id: 'localStorageSet', tab: 'Storage', icon: '💾', name: 'Set LocalStorage',
    description: 'Set a key-value pair in the page\'s localStorage',
    fields: [
      { id: 'key',   label: 'Key', type: 'text', placeholder: 'user_token', required: true },
      { id: 'value', label: 'Value', type: 'text', placeholder: 'abc123', required: true },
    ],
    buildSteps: (v) => [step({ type: 'script', label: `localStorage.${v.key ?? ''}="${v.value ?? ''}"`,
      code: `localStorage.setItem(${JSON.stringify(v.key??'')},${JSON.stringify(v.value??'')})` })],
  },
  {
    id: 'localStorageGet', tab: 'Storage', icon: '📤', name: 'Get LocalStorage',
    description: 'Read a value from localStorage and store it in a variable',
    fields: [
      { id: 'key',     label: 'Key', type: 'text', placeholder: 'user_token', required: true },
      { id: 'storeAs', label: 'Store as variable', type: 'variable', placeholder: 'token', required: true },
    ],
    buildSteps: (v) => [step({ type: 'script', label: `Get localStorage.${v.key ?? ''} → ${v.storeAs ?? ''}`,
      code: `localStorage.getItem(${JSON.stringify(v.key??'')})`, storeAs: v.storeAs })],
  },
  {
    id: 'localStorageClear', tab: 'Storage', icon: '🗑️', name: 'Clear LocalStorage',
    description: 'Clear all localStorage data for the current origin',
    fields: [],
    buildSteps: () => [step({ type: 'script', label: 'Clear localStorage', code: `localStorage.clear()` })],
  },
  {
    id: 'sessionStorageSet', tab: 'Storage', icon: '🔐', name: 'Set SessionStorage',
    description: 'Set a key-value pair in the page\'s sessionStorage',
    fields: [
      { id: 'key',   label: 'Key', type: 'text', placeholder: 'session_id', required: true },
      { id: 'value', label: 'Value', type: 'text', placeholder: 'xyz789', required: true },
    ],
    buildSteps: (v) => [step({ type: 'script', label: `sessionStorage.${v.key ?? ''}="${v.value ?? ''}"`,
      code: `sessionStorage.setItem(${JSON.stringify(v.key??'')},${JSON.stringify(v.value??'')})` })],
  },
  {
    id: 'sessionStorageGet', tab: 'Storage', icon: '📤', name: 'Get SessionStorage',
    description: 'Read a value from sessionStorage and store it in a variable',
    fields: [
      { id: 'key',     label: 'Key', type: 'text', placeholder: 'session_id', required: true },
      { id: 'storeAs', label: 'Store as variable', type: 'variable', placeholder: 'sessionVal', required: true },
    ],
    buildSteps: (v) => [step({ type: 'script', label: `Get sessionStorage.${v.key ?? ''} → ${v.storeAs ?? ''}`,
      code: `sessionStorage.getItem(${JSON.stringify(v.key??'')})`, storeAs: v.storeAs })],
  },
  {
    id: 'sessionStorageClear', tab: 'Storage', icon: '🗑️', name: 'Clear SessionStorage',
    description: 'Clear all sessionStorage data for the current origin',
    fields: [],
    buildSteps: () => [step({ type: 'script', label: 'Clear sessionStorage', code: `sessionStorage.clear()` })],
  },
  {
    id: 'cookieGet', tab: 'Storage', icon: '🍪', name: 'Read Cookie',
    description: 'Read a cookie value and store it in a variable',
    fields: [
      { id: 'name',    label: 'Cookie name', type: 'text', placeholder: 'auth_token', required: true },
      { id: 'storeAs', label: 'Store as variable', type: 'variable', placeholder: 'cookieVal', required: true },
    ],
    buildSteps: (v) => [step({ type: 'script', label: `Cookie ${v.name ?? ''} → ${v.storeAs ?? ''}`,
      code: `(function(){var m=document.cookie.match(new RegExp('(?:^|;\\\\s*)'+${JSON.stringify(v.name??'')}+'=([^;]*)'));return m?decodeURIComponent(m[1]):null;})()`,
      storeAs: v.storeAs })],
  },
  {
    id: 'cookieSet', tab: 'Storage', icon: '🍪', name: 'Set Cookie',
    description: 'Set a cookie value for the current page',
    fields: [
      { id: 'name',  label: 'Cookie name', type: 'text', placeholder: 'auth_token', required: true },
      { id: 'value', label: 'Value', type: 'text', placeholder: 'mytoken123', required: true },
      { id: 'days',  label: 'Expires (days, 0=session)', type: 'number', defaultValue: '0' },
    ],
    buildSteps: (v) => {
      const days = parseInt(v.days ?? '0', 10);
      const expires = days > 0 ? `;expires=${new Date(Date.now() + days * 864e5).toUTCString()}` : '';
      return [step({ type: 'script', label: `Set cookie ${v.name ?? ''}`,
        code: `document.cookie=${JSON.stringify(`${v.name??''}=${encodeURIComponent(v.value??'')}${expires};path=/`)}` })];
    },
  },
  {
    id: 'cookieDelete', tab: 'Storage', icon: '🗑️', name: 'Delete Cookie',
    description: 'Delete a specific cookie by name',
    fields: [{ id: 'name', label: 'Cookie name', type: 'text', placeholder: 'auth_token', required: true }],
    buildSteps: (v) => [step({ type: 'script', label: `Delete cookie: ${v.name ?? ''}`,
      code: `document.cookie=${JSON.stringify(`${v.name??''}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`)}` })],
  },
  {
    id: 'cookieClearAll', tab: 'Storage', icon: '🧹', name: 'Clear All Cookies',
    description: 'Delete all cookies for the current page origin',
    fields: [],
    buildSteps: () => [step({ type: 'script', label: 'Clear all cookies',
      code: `document.cookie.split(';').forEach(function(c){document.cookie=c.replace(/^ +/,'').replace(/=.*/,'=;expires='+new Date(0).toUTCString()+';path=/');})` })],
  },
];

// ─── Frames / iframes ────────────────────────────────────────────────────────

const FRAME_COMMANDS: WebCommand[] = [
  {
    id: 'frameEnter', tab: 'Frames', icon: '🖼️', name: 'Enter iFrame',
    description: 'Scope subsequent steps to an iframe. All actions after this step target the iframe until "Exit iFrame" is added.',
    fields: [
      { id: 'selector', label: 'iFrame element', type: 'selector', required: true,
        hint: 'Pick the <iframe> element on the mirror, or enter its CSS selector' },
      { id: 'label', label: 'Label (optional)', type: 'text', placeholder: 'Enter payment iframe' },
    ],
    buildSteps: (v) => [step({
      type: 'frameScope',
      label: v.label || `Enter iframe: ${parseSelector(v.selector ?? '').description}`,
      selector: parseSelector(v.selector ?? ''),
      steps: [],
    })],
  },
  {
    id: 'frameGetText', tab: 'Frames', icon: '📄', name: 'Get Text from iFrame',
    description: 'Extract text from an element inside an iframe',
    fields: [
      { id: 'frameSel',  label: 'iFrame element', type: 'selector', required: true },
      { id: 'innerSel',  label: 'Element inside iframe (CSS)', type: 'text', placeholder: '#result-text', required: true },
      { id: 'storeAs',   label: 'Store as variable', type: 'variable', placeholder: 'frameText', required: true },
    ],
    buildSteps: (v) => {
      const fSel = parseSelector(v.frameSel ?? '');
      const fCss = JSON.stringify(fSel.candidates[0]?.value ?? 'iframe');
      const iSel = JSON.stringify(v.innerSel ?? 'body');
      return [step({ type: 'script', label: `Get iframe text → ${v.storeAs ?? ''}`,
        code: `(function(){var f=document.querySelector(${fCss});var doc=f&&(f.contentDocument||f.contentWindow&&f.contentWindow.document);var el=doc&&doc.querySelector(${iSel});return el?el.innerText||el.textContent:null;})()`,
        storeAs: v.storeAs })];
    },
  },
  {
    id: 'frameClickElement', tab: 'Frames', icon: '👆', name: 'Click inside iFrame',
    description: 'Click an element that is inside an iframe',
    fields: [
      { id: 'frameSel', label: 'iFrame element', type: 'selector', required: true },
      { id: 'innerSel', label: 'Element inside iframe (CSS)', type: 'text', placeholder: 'button.submit', required: true },
    ],
    buildSteps: (v) => {
      const fSel = parseSelector(v.frameSel ?? '');
      const fCss = JSON.stringify(fSel.candidates[0]?.value ?? 'iframe');
      const iSel = JSON.stringify(v.innerSel ?? 'button');
      return [step({ type: 'script', label: `Click in iframe: ${v.innerSel ?? ''}`,
        code: `(function(){var f=document.querySelector(${fCss});var doc=f&&(f.contentDocument||f.contentWindow&&f.contentWindow.document);var el=doc&&doc.querySelector(${iSel});if(el)el.click();else throw new Error('Element ${v.innerSel} not found in iframe');})()` })];
    },
  },
  {
    id: 'frameTypeText', tab: 'Frames', icon: '⌨️', name: 'Type into iFrame Input',
    description: 'Type text into an input field that is inside an iframe',
    fields: [
      { id: 'frameSel', label: 'iFrame element', type: 'selector', required: true },
      { id: 'innerSel', label: 'Input element inside iframe (CSS)', type: 'text', placeholder: 'input[name="card"]', required: true },
      { id: 'text',     label: 'Text to type', type: 'text', placeholder: '4111111111111111', required: true },
    ],
    buildSteps: (v) => {
      const fSel = parseSelector(v.frameSel ?? '');
      const fCss = JSON.stringify(fSel.candidates[0]?.value ?? 'iframe');
      const iSel = JSON.stringify(v.innerSel ?? 'input');
      const txt  = JSON.stringify(v.text ?? '');
      return [step({ type: 'script', label: `Type in iframe: ${v.innerSel ?? ''} = ${v.text ?? ''}`,
        code: `(function(){var f=document.querySelector(${fCss});var doc=f&&(f.contentDocument||f.contentWindow&&f.contentWindow.document);var el=doc&&doc.querySelector(${iSel});if(el){el.focus();el.value=${txt};el.dispatchEvent(new Event('input',{bubbles:true}));el.dispatchEvent(new Event('change',{bubbles:true}));}else throw new Error('Input not found in iframe');})()` })];
    },
  },
  {
    id: 'frameAssertVisible', tab: 'Frames', icon: '👁️', name: 'Assert Visible in iFrame',
    description: 'Assert that an element inside an iframe is visible',
    fields: [
      { id: 'frameSel', label: 'iFrame element', type: 'selector', required: true },
      { id: 'innerSel', label: 'Element inside iframe (CSS)', type: 'text', placeholder: '.success-message', required: true },
    ],
    buildSteps: (v) => {
      const fSel = parseSelector(v.frameSel ?? '');
      const fCss = JSON.stringify(fSel.candidates[0]?.value ?? 'iframe');
      const iSel = JSON.stringify(v.innerSel ?? 'body');
      return [step({ type: 'script', label: `Assert visible in iframe: ${v.innerSel ?? ''}`,
        code: `(function(){var f=document.querySelector(${fCss});var doc=f&&(f.contentDocument||f.contentWindow&&f.contentWindow.document);var el=doc&&doc.querySelector(${iSel});if(!el||el.offsetParent===null)throw new Error('Element ${v.innerSel} not visible in iframe');})()` })];
    },
  },
  {
    id: 'frameEvalJS', tab: 'Frames', icon: '{ }', name: 'Run JS in iFrame',
    description: 'Execute arbitrary JavaScript inside an iframe\'s document context',
    fields: [
      { id: 'frameSel', label: 'iFrame element', type: 'selector', required: true },
      { id: 'code',     label: 'JavaScript (runs inside the iframe)', type: 'code',
        placeholder: "document.querySelector('#submit').click();", required: true },
      { id: 'storeAs',  label: 'Store result as (optional)', type: 'variable', placeholder: 'result' },
    ],
    buildSteps: (v) => {
      const fSel = parseSelector(v.frameSel ?? '');
      const fCss = JSON.stringify(fSel.candidates[0]?.value ?? 'iframe');
      const inner = (v.code ?? '').replace(/`/g, '\\`');
      return [step({ type: 'script',
        label: `JS in iframe: ${(v.code ?? '').slice(0, 30)}`,
        code: `(function(){var f=document.querySelector(${fCss});var doc=f&&(f.contentDocument||f.contentWindow&&f.contentWindow.document);if(!doc)throw new Error('Cannot access iframe document');return (function(){${inner}}).call(doc);})()`,
        storeAs: v.storeAs || undefined })];
    },
  },
];

// ─── Dialog commands ──────────────────────────────────────────────────────────

const DIALOG_COMMANDS: WebCommand[] = [
  {
    id: 'acceptDialog', tab: 'Input', icon: '💬', name: 'Accept Dialog',
    description: 'Accept an alert, confirm, or prompt dialog (OK button)',
    fields: [{ id: 'promptText', label: 'Prompt response (optional — for prompt() dialogs)', type: 'text', placeholder: 'Type text to fill into prompt' }],
    buildSteps: (v) => {
      const promptVal = v.promptText ? JSON.stringify(v.promptText) : 'undefined';
      return [step({ type: 'script', label: 'Accept dialog',
        code: `(function(){var orig=window.alert;window.alert=function(){};var origC=window.confirm;window.confirm=function(){return true;};var origP=window.prompt;window.prompt=function(){return ${promptVal};};setTimeout(function(){window.alert=orig;window.confirm=origC;window.prompt=origP;},5000);})()` })];
    },
  },
  {
    id: 'dismissDialog', tab: 'Input', icon: '❌', name: 'Dismiss Dialog',
    description: 'Dismiss/cancel an alert, confirm, or prompt dialog',
    fields: [],
    buildSteps: () => [step({ type: 'script', label: 'Dismiss dialog',
      code: `(function(){var orig=window.confirm;window.confirm=function(){return false;};var origP=window.prompt;window.prompt=function(){return null;};setTimeout(function(){window.confirm=orig;window.prompt=origP;},5000);})()` })],
  },
];

// ─── Control flow commands ────────────────────────────────────────────────────
// Phase 3: Script-based control flow that works NOW in the Studio.
// Phase 4: Visual block editor where you drag steps INTO if/repeat/forEach blocks.

const CONTROL_COMMANDS: WebCommand[] = [
  {
    id: 'controlIf', tab: 'Control', icon: '❓', name: 'If / Else',
    description: 'Run different code based on a condition. Write "Then" JS and optionally "Else" JS. Both branches run in the same script step — the runner evaluates the condition and executes the matching branch.',
    fields: [
      { id: 'condition', label: 'Condition (JS — must return true/false)', type: 'code',
        placeholder: "document.querySelector('.login-btn') !== null", required: true },
      { id: 'thenCode',  label: 'Then — runs if condition is TRUE', type: 'code',
        placeholder: "document.querySelector('.login-btn').click();", required: true },
      { id: 'elseCode',  label: 'Else — runs if condition is FALSE (optional)', type: 'code',
        placeholder: "console.log('Element not found');" },
      { id: 'label',     label: 'Label', type: 'text', placeholder: 'If login button visible' },
    ],
    buildSteps: (v) => {
      const cond     = (v.condition ?? 'false').trim();
      const thenCode = (v.thenCode  ?? '').trim();
      const elseCode = (v.elseCode  ?? '').trim();
      const elseBlock = elseCode ? ` else {\n  ${elseCode}\n}` : '';
      const code = `(function(){\n  if (${cond}) {\n    ${thenCode}\n  }${elseBlock}\n})()`;
      return [step({ type: 'script', label: v.label || `If: ${cond.slice(0, 40)}`, code })];
    },
  },
  {
    id: 'controlIfElement', tab: 'Control', icon: '🔍', name: 'If Element Visible',
    description: 'Check if an element is present and visible, then run different steps. Pick element from mirror.',
    fields: [
      { id: 'selector',  label: 'Element to check', type: 'selector', required: true },
      { id: 'thenCode',  label: 'Then — JS to run if VISIBLE', type: 'code',
        placeholder: "document.querySelector('.buy-btn').click();" },
      { id: 'elseCode',  label: 'Else — JS to run if NOT visible (optional)', type: 'code',
        placeholder: "console.log('Element not visible');" },
      { id: 'label',     label: 'Label', type: 'text', placeholder: 'If buy button visible' },
    ],
    buildSteps: (v) => {
      const sel      = parseSelector(v.selector ?? '');
      const cssSel   = JSON.stringify(sel.candidates[0]?.value ?? 'body');
      const thenCode = (v.thenCode ?? '').trim();
      const elseCode = (v.elseCode ?? '').trim();
      const elseBlock = elseCode ? ` else {\n    ${elseCode}\n  }` : '';
      const code = `(function(){\n  var el=document.querySelector(${cssSel});\n  var visible=el&&el.offsetParent!==null&&el.getBoundingClientRect().width>0;\n  if(visible){\n    ${thenCode}\n  }${elseBlock}\n})()`;
      return [step({ type: 'script', label: v.label || `If visible: ${sel.description}`, code })];
    },
  },
  {
    id: 'controlRepeat', tab: 'Control', icon: '🔁', name: 'Repeat N Times',
    description: 'Repeat a JavaScript block N times. Use the loop index variable "i" (0-based) inside the code.',
    fields: [
      { id: 'count', label: 'Number of iterations', type: 'number', placeholder: '3', required: true, defaultValue: '3' },
      { id: 'code',  label: 'JS to run each iteration (use "i" for index)', type: 'code',
        placeholder: "console.log('Iteration ' + i);\ndocument.querySelector('.next-btn').click();", required: true },
      { id: 'delay', label: 'Delay between iterations (ms)', type: 'number', defaultValue: '500' },
      { id: 'label', label: 'Label', type: 'text', placeholder: 'Repeat 3 times' },
    ],
    buildSteps: (v) => {
      const n     = parseInt(v.count ?? '3', 10);
      const delay = parseInt(v.delay ?? '500', 10);
      const inner = (v.code ?? '').trim();
      const code  = delay > 0
        ? `(async function(){for(var i=0;i<${n};i++){${inner};await new Promise(r=>setTimeout(r,${delay}));}})()`
        : `(function(){for(var i=0;i<${n};i++){${inner};}})()`;
      return [step({ type: 'script', label: v.label || `Repeat ${n}×`, code })];
    },
  },
  {
    id: 'controlWhile', tab: 'Control', icon: '🔄', name: 'While Loop',
    description: 'Keep repeating JS while a condition is true. Max iterations prevents infinite loops.',
    fields: [
      { id: 'condition', label: 'Continue while (JS expression)', type: 'code',
        placeholder: "document.querySelectorAll('.item').length > 0", required: true },
      { id: 'code',      label: 'JS to run each iteration', type: 'code',
        placeholder: "document.querySelector('.load-more').click();" },
      { id: 'maxIter',   label: 'Max iterations (safety limit)', type: 'number', defaultValue: '20' },
      { id: 'delay',     label: 'Delay between iterations (ms)', type: 'number', defaultValue: '1000' },
      { id: 'label',     label: 'Label', type: 'text', placeholder: 'While more items exist' },
    ],
    buildSteps: (v) => {
      const cond    = (v.condition ?? 'false').trim();
      const inner   = (v.code ?? '').trim();
      const maxIter = parseInt(v.maxIter ?? '20', 10);
      const delay   = parseInt(v.delay   ?? '1000', 10);
      const code    = `(async function(){var __n=0;while((${cond})&&__n<${maxIter}){${inner};__n++;await new Promise(r=>setTimeout(r,${delay}));console.log('[Plex While] iteration '+__n);}})()`;
      return [step({ type: 'script', label: v.label || `While: ${cond.slice(0, 35)}`, code })];
    },
  },
  {
    id: 'controlForEach', tab: 'Control', icon: '📋', name: 'For Each (JS Array)',
    description: 'Iterate over a JSON array and run JS for each item. The current item is available as "item".',
    fields: [
      { id: 'arrayExpr', label: 'Array expression (JS or JSON)', type: 'code',
        placeholder: '["alice@test.com","bob@test.com"] or window.__plex_vars.users', required: true },
      { id: 'code',      label: 'JS to run per item (use "item" and "i")', type: 'code',
        placeholder: "document.querySelector('#email').value=item;\ndocument.querySelector('#submit').click();", required: true },
      { id: 'delay',     label: 'Delay between items (ms)', type: 'number', defaultValue: '500' },
      { id: 'label',     label: 'Label', type: 'text', placeholder: 'For each email' },
    ],
    buildSteps: (v) => {
      const arr   = (v.arrayExpr ?? '[]').trim();
      const inner = (v.code ?? '').trim();
      const delay = parseInt(v.delay ?? '500', 10);
      const code  = `(async function(){var __arr=(${arr});for(var i=0;i<__arr.length;i++){var item=__arr[i];${inner};await new Promise(r=>setTimeout(r,${delay}));}})()`;
      return [step({ type: 'script', label: v.label || `For each item`, code })];
    },
  },
  {
    id: 'controlSwitch', tab: 'Control', icon: '🔀', name: 'Switch / Match',
    description: 'Run different JS based on the value of a variable or expression.',
    fields: [
      { id: 'expression', label: 'Value to switch on (JS expression)', type: 'code',
        placeholder: "window.__plex_vars.status", required: true },
      { id: 'cases',      label: 'Cases (format: value | JS code to run), one per line', type: 'code',
        placeholder: 'success | document.querySelector(".ok").click();\nerror | document.querySelector(".retry").click();\ndefault | console.log("unknown");', required: true },
      { id: 'label',      label: 'Label', type: 'text', placeholder: 'Switch on status' },
    ],
    buildSteps: (v) => {
      const lines = (v.cases ?? '').split('\n').filter(Boolean);
      const cases = lines.map((line) => {
        const pipe = line.indexOf('|');
        const val  = pipe >= 0 ? line.slice(0, pipe).trim() : line.trim();
        const code = pipe >= 0 ? line.slice(pipe + 1).trim() : '';
        return val === 'default'
          ? `default: {${code};break;}`
          : `case ${JSON.stringify(val)}: {${code};break;}`;
      }).join('\n  ');
      const code = `(function(){switch(String(${(v.expression ?? '"?"').trim()})){\n  ${cases}\n}})()`;
      return [step({ type: 'script', label: v.label || `Switch: ${(v.expression ?? '').slice(0, 30)}`, code })];
    },
  },
  {
    id: 'controlCallFlow', tab: 'Control', icon: '📞', name: 'Call Flow',
    description: 'Call a reusable flow from the Functions library by its ID.',
    fields: [
      { id: 'flowId',   label: 'Flow ID', type: 'text', placeholder: 'login_flow', required: true },
      { id: 'flowName', label: 'Display name', type: 'text', placeholder: 'Login Flow' },
      { id: 'label',    label: 'Step label', type: 'text', placeholder: 'Run login flow' },
    ],
    buildSteps: (v) => [step({
      type: 'callFlow',
      label: v.label || `Call: ${v.flowName ?? v.flowId ?? ''}`,
      sharedFlowId:   v.flowId   ?? '',
      sharedFlowName: v.flowName ?? v.flowId ?? '',
      args: {},
    })],
  },
];

// ─── Device / Emulation commands ─────────────────────────────────────────────

const DEVICE_COMMANDS: WebCommand[] = [
  {
    id: 'setViewport', tab: 'Device', icon: '📐', name: 'Set Viewport Size',
    description: 'Resize the browser viewport to a specific width × height (via JS — use CLI for Playwright-level resizing)',
    fields: [
      { id: 'width',  label: 'Width (px)',  type: 'number', placeholder: '1280', required: true, defaultValue: '1280' },
      { id: 'height', label: 'Height (px)', type: 'number', placeholder: '720',  required: true, defaultValue: '720'  },
    ],
    buildSteps: (v) => {
      const w = parseInt(v.width ?? '1280', 10);
      const h = parseInt(v.height ?? '720', 10);
      return [step({ type: 'script', label: `Viewport ${w}×${h}`,
        code: `Object.defineProperty(window,'innerWidth',{get:()=>${w},configurable:true});Object.defineProperty(window,'innerHeight',{get:()=>${h},configurable:true});window.dispatchEvent(new Event('resize'))` })];
    },
  },
  {
    id: 'mobilePreset', tab: 'Device', icon: '📱', name: 'Mobile Preset',
    description: 'Switch to a common mobile viewport size preset',
    fields: [
      { id: 'preset', label: 'Device preset', type: 'select', required: true, defaultValue: 'iphone15',
        options: [
          { value: 'iphone15',    label: 'iPhone 15 (390×844)'       },
          { value: 'iphone15pro', label: 'iPhone 15 Pro (393×852)'   },
          { value: 'pixel7',      label: 'Pixel 7 (412×915)'         },
          { value: 'galaxys24',   label: 'Galaxy S24 (360×780)'      },
          { value: 'ipad',        label: 'iPad (810×1080)'           },
          { value: 'ipadpro',     label: 'iPad Pro 12.9" (1024×1366)'},
          { value: 'desktop1080', label: 'Desktop 1080p (1920×1080)' },
          { value: 'desktop720',  label: 'Desktop 720p (1280×720)'   },
        ],
      },
    ],
    buildSteps: (v) => {
      const dims: Record<string, [number, number]> = {
        iphone15: [390, 844], iphone15pro: [393, 852], pixel7: [412, 915],
        galaxys24: [360, 780], ipad: [810, 1080], ipadpro: [1024, 1366],
        desktop1080: [1920, 1080], desktop720: [1280, 720],
      };
      const [w, h] = dims[v.preset ?? 'iphone15'] ?? [390, 844];
      return [step({ type: 'script', label: `Preset: ${v.preset ?? 'iphone15'} (${w}×${h})`,
        code: `Object.defineProperty(window,'innerWidth',{get:()=>${w},configurable:true});Object.defineProperty(window,'innerHeight',{get:()=>${h},configurable:true});window.dispatchEvent(new Event('resize'))` })];
    },
  },
  {
    id: 'setUserAgent', tab: 'Device', icon: '🤖', name: 'Set User Agent',
    description: 'Override the browser\'s User-Agent string',
    fields: [
      { id: 'ua', label: 'User-Agent string', type: 'text',
        placeholder: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X)...', required: true },
    ],
    buildSteps: (v) => [step({ type: 'script', label: `UA: ${(v.ua ?? '').slice(0, 40)}…`,
      code: `Object.defineProperty(navigator,'userAgent',{get:function(){return ${JSON.stringify(v.ua??'')};},configurable:true})` })],
  },
  {
    id: 'setGeolocation', tab: 'Device', icon: '📍', name: 'Set Geolocation',
    description: 'Mock the browser\'s GPS location (overrides navigator.geolocation)',
    fields: [
      { id: 'lat', label: 'Latitude',  type: 'text', placeholder: '37.7749', required: true },
      { id: 'lng', label: 'Longitude', type: 'text', placeholder: '-122.4194', required: true },
      { id: 'acc', label: 'Accuracy (metres)', type: 'number', defaultValue: '10' },
    ],
    buildSteps: (v) => [step({ type: 'script', label: `Geolocation (${v.lat ?? 0}, ${v.lng ?? 0})`,
      code: `(function(){var pos={coords:{latitude:${v.lat??0},longitude:${v.lng??0},accuracy:${parseInt(v.acc??'10',10)},altitude:null,altitudeAccuracy:null,heading:null,speed:null},timestamp:Date.now()};navigator.geolocation.getCurrentPosition=function(s){s(pos);};navigator.geolocation.watchPosition=function(s){s(pos);return 0;};})()` })],
  },
  {
    id: 'darkMode', tab: 'Device', icon: '🌙', name: 'Dark / Light Mode',
    description: 'Simulate prefers-color-scheme media query',
    fields: [
      { id: 'scheme', label: 'Color scheme', type: 'select', defaultValue: 'dark',
        options: [{ value: 'dark', label: 'Dark mode' }, { value: 'light', label: 'Light mode' }] },
    ],
    buildSteps: (v) => [step({ type: 'script', label: `${v.scheme ?? 'dark'} mode`,
      code: `(function(){var style=document.getElementById('__plex_scheme')||Object.assign(document.createElement('style'),{id:'__plex_scheme'});document.head.appendChild(style);style.textContent='@media(prefers-color-scheme:${v.scheme==='dark'?'light':'dark'}){:root{}}';})()` })],
  },
  {
    id: 'offlineMode', tab: 'Device', icon: '✈️', name: 'Offline Mode',
    description: 'Simulate going offline / coming back online (IR setDeviceState wifi flag)',
    fields: [
      { id: 'offline', label: 'Network state', type: 'select', defaultValue: 'offline',
        options: [{ value: 'offline', label: 'Go offline' }, { value: 'online', label: 'Go online' }] },
    ],
    buildSteps: (v) => [step({
      type: 'setDeviceState',
      label: v.offline === 'offline' ? 'Set offline' : 'Set online',
      wifi: v.offline !== 'offline',
    })],
  },
  {
    id: 'elementScreenshot', tab: 'Device', icon: '🖼️', name: 'Element Screenshot',
    description: 'Capture a screenshot of a specific element only',
    fields: [
      { id: 'selector', label: 'Element', type: 'selector', required: true },
      { id: 'label',    label: 'Screenshot label', type: 'text', placeholder: 'hero-banner' },
    ],
    buildSteps: (v) => {
      const sel = parseSelector(v.selector ?? '');
      const cssSel = JSON.stringify(sel.candidates[0]?.value ?? 'body');
      return [step({ type: 'script', label: `Screenshot: ${sel.description}`,
        code: `(function(){var e=document.querySelector(${cssSel});if(e){e.scrollIntoView({block:'center'});}})()` }),
        step({ type: 'screenshot', label: v.label || `Element: ${sel.description}` })];
    },
  },
];

const SCRIPT_COMMANDS: WebCommand[] = [
  {
    id: 'executeJS', tab: 'Script', icon: '{ }', name: 'JavaScript Command',
    description: 'Execute arbitrary JavaScript in the browser — return value stored optionally',
    fields: [
      { id: 'code',    label: 'JavaScript', type: 'code', placeholder: "document.querySelector('button').click();", required: true },
      { id: 'storeAs', label: 'Store result as (optional)', type: 'variable', placeholder: 'result' },
    ],
    buildSteps: (v) => [step({ type: 'script', label: `JS: ${(v.code ?? '').slice(0, 40)}…`, code: v.code ?? '', storeAs: v.storeAs || undefined })],
  },
];

const REPORT_COMMANDS: WebCommand[] = [
  {
    id: 'screenshot', tab: 'Report', icon: '📷', name: 'Screenshot',
    description: 'Capture a screenshot at this point — saved to the report',
    fields: [],
    buildSteps: () => [step({ type: 'screenshot', label: 'Screenshot' })],
  },
  {
    id: 'markPass', tab: 'Report', icon: '✅', name: 'Mark Pass',
    description: 'Explicitly mark a checkpoint as passed',
    fields: [{ id: 'message', label: 'Message', type: 'text', placeholder: 'Login flow verified', required: true }],
    buildSteps: (v) => [step({ type: 'checkpoint', label: `✓ Pass: ${v.message ?? ''}`, message: v.message ?? '', status: 'pass' })],
  },
  {
    id: 'markFail', tab: 'Report', icon: '❌', name: 'Mark Fail',
    description: 'Explicitly mark a checkpoint as failed (halts the test)',
    fields: [{ id: 'message', label: 'Reason', type: 'text', placeholder: 'Expected element was not found', required: true }],
    buildSteps: (v) => [step({ type: 'checkpoint', label: `✗ Fail: ${v.message ?? ''}`, message: v.message ?? '', status: 'fail' })],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Exported registry
// ─────────────────────────────────────────────────────────────────────────────

export const WEB_COMMANDS: WebCommand[] = [
  ...NAVIGATE_COMMANDS,
  ...INPUT_COMMANDS,
  ...INPUT_EXTRA_COMMANDS,
  ...DIALOG_COMMANDS,
  ...KEYS_COMMANDS,
  ...ASSERT_COMMANDS,
  ...WAIT_COMMANDS,
  ...SCROLL_COMMANDS,
  ...DATA_COMMANDS,
  ...STORAGE_COMMANDS,
  ...FRAME_COMMANDS,
  ...SCRIPT_COMMANDS,
  ...REPORT_COMMANDS,
  ...CONTROL_COMMANDS,
  ...DEVICE_COMMANDS,
];

export const COMMAND_TABS = [
  { id: 'Navigate',  label: 'Navigate',  icon: '🧭', active: true },
  { id: 'Input',     label: 'Input',     icon: '👆', active: true },
  { id: 'Keys',      label: 'Keys',      icon: '⌨️', active: true },
  { id: 'Assert',    label: 'Assert',    icon: '✅', active: true },
  { id: 'Wait',      label: 'Wait',      icon: '⏳', active: true },
  { id: 'Scroll',    label: 'Scroll',    icon: '🖱️', active: true },
  { id: 'Variables', label: 'Variables', icon: '📦', active: true },
  { id: 'Storage',   label: 'Storage',   icon: '💾', active: true },
  { id: 'Frames',    label: 'Frames',    icon: '🖼️', active: true },
  { id: 'Script',    label: 'Script',    icon: '⚙️', active: true },
  { id: 'Report',    label: 'Report',    icon: '📊', active: true },
  { id: 'Control',   label: 'Control',   icon: '🔀', active: true },
  { id: 'Device',    label: 'Device',    icon: '📱', active: true },
];

export function getCommandsForTab(tab: string): WebCommand[] {
  return WEB_COMMANDS.filter(c => c.tab === tab);
}
